import React, { useRef, forwardRef, useState, useImperativeHandle, useEffect } from 'react';
import styled from 'styled-components';
import { useJsApiLoader } from '@react-google-maps/api';

const libraries = ['places'];

const GoogleAddressModal = forwardRef((props, ref) => {
  const { exitClick } = props;

  const TopLevelRef = useRef();

  const width = 280;
  const height = 450;

  // 입력/결과 상태
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [statusText, setStatusText] = useState('');
  const [isOpen, setIsOpen] = useState(false); // 모달 열렸는지 (예측 요청 제어용)

  // google 서비스 객체
  const autoServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const placesDummyDivRef = useRef(null);

  // 스크롤 잠금 (카카오 모달과 동일)
  useEffect(() => {
    if (!isOpen) return;

    document.body.style.cssText = `position: fixed; top: -${window.scrollY}px`;
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.cssText = `position: ""; top: "";`;
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, [isOpen]);

  // Google script 로드
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-places-custom-list',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
    language: 'en',
  });

  // 서비스 초기화
  useEffect(() => {
    if (!isLoaded) return;
    if (!window.google?.maps?.places) return;

    autoServiceRef.current = new window.google.maps.places.AutocompleteService();

    // PlacesService는 Map 또는 HTMLElement가 필요해서 dummy div로 생성
    if (!placesDummyDivRef.current) return;
    placesServiceRef.current = new window.google.maps.places.PlacesService(placesDummyDivRef.current);
  }, [isLoaded]);

  // 입력 디바운스 후 predictions 불러오기
  useEffect(() => {
    if (!isOpen) return;
    if (!isLoaded || loadError) return;

    const q = query.trim();
    if (q.length === 0) {
      setPredictions([]);
      setStatusText('Please enter an address');
      return;
    }

    // 너무 짧으면 요청하지 않기
    if (q.length < 2) {
      setPredictions([]);
      setStatusText('Please enter at least 2 characters');
      return;
    }

    setStatusText('Searching...');

    const timer = setTimeout(() => {
      const svc = autoServiceRef.current;
      if (!svc) return;

      svc.getPlacePredictions(
        {
          input: q,
          types: ['geocode'],
        },
        (results, status) => {
          const OK = window.google.maps.places.PlacesServiceStatus.OK;

          if (status !== OK || !results || results.length === 0) {
            setPredictions([]);
            setStatusText('No results found');
            return;
          }

          setPredictions(results);
          setStatusText('');
        }
      );
    }, 250);

    return () => clearTimeout(timer);
  }, [query, isOpen, isLoaded, loadError]);

  // placeId로 상세 주소 가져오기
  const selectPrediction = (p) => {
    const placeId = p.place_id;
    const svc = placesServiceRef.current;

    if (!svc || !placeId) return;

    setStatusText('Loading details...');
    setPredictions([]); // 클릭 순간 리스트 잠깐 비우기(선택 피드백)

    svc.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'name'],
      },
      (place, status) => {
        const OK = window.google.maps.places.PlacesServiceStatus.OK;

        if (status !== OK || !place) {
          setStatusText('Could not fetch the address');
          return;
        }

        const addr = place.formatted_address || place.name || '';
        if (addr) exitClick(addr);
        else setStatusText('Invalid address format');
      }
    );
  };

  // 모달 닫기(선택 없이 닫기)
  const closeModal = () => {
    exitClick(undefined);
  };

  // 부모에서 제어하는 API
  useImperativeHandle(ref, () => ({
    setOpacity: (val) => {
      const el = TopLevelRef.current;
      if (!el) return;

      if (val === 0) {
        el.style.zIndex = -300;
        el.style.opacity = 0;
        setIsOpen(false);
        return;
      }

      el.style.zIndex = 300;
      el.style.opacity = 1;
      setIsOpen(true);

      // 열릴 때 초기화
      setQuery('');
      setPredictions([]);
      setStatusText('Please enter an address.');
    },
  }));

  return (
    <TopLevelWrapper ref={TopLevelRef}>
      <ModalWrapper width={width + 20} height={height + 32}>
        <TopBarWrapper width={width + 20}>
          <ExitButton onClick={closeModal}>Close</ExitButton>
        </TopBarWrapper>

        <BodyWrapper width={width + 20} height={height}>
          {loadError ? (
            <StatusBox>Failed to load Google address search</StatusBox>
          ) : !isLoaded ? (
            <StatusBox>Loding...</StatusBox>
          ) : (
            <>
              <SearchRow>
                <SearchInput
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for an address (street, city, etc.)"
                  autoComplete="off"
                />
              </SearchRow>

              {predictions.length === 0 && (
                <TipBox>
                    <TipTitle>Tip</TipTitle>
                    <TipText>Try: "street + number" (e.g., 1600 Amphitheatre Pkwy)</TipText>
                    <TipText>Or: "city + street" (e.g., Shibuya City, Jingumae)</TipText>
                    <TipText>You can also search places/buildings</TipText>
                </TipBox>
              )}

              <ResultWrapper hasPredictions={predictions.length > 0}>
                {statusText && predictions.length === 0 ? <StatusText>{statusText}</StatusText> : null}

                {predictions.length > 0 ? (
                  <ResultList>
                    {predictions.map((p) => (
                      <ResultItem key={p.place_id} onClick={() => selectPrediction(p)}>
                        <MainText>{p.structured_formatting?.main_text || p.description}</MainText>
                        <SubText>{p.structured_formatting?.secondary_text || ''}</SubText>
                      </ResultItem>
                    ))}
                  </ResultList>
                ) : null}
              </ResultWrapper>

              {/* PlacesService용 dummy element */}
              <div ref={placesDummyDivRef} style={{ display: 'none' }} />
            </>
          )}
        </BodyWrapper>
      </ModalWrapper>
    </TopLevelWrapper>
  );
});

export default GoogleAddressModal;

/* ===== styles (카카오 모달 느낌으로 구성) ===== */

const TopLevelWrapper = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  width: 100vw;
  height: 100vh;

  z-index: -300;
  opacity: 0;

  background-color: rgba(0, 0, 0, 0.6);
  transition: all 0.3s ease-in-out;
`;

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
`;

const TopBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: ${(p) => p.width}px;
  background-color: #ff8b48;
`;

const ExitButton = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;
  color: #ffffff;
  margin: 0 10px 0 0;

  &:hover {
    cursor: pointer;
  }
`;

const BodyWrapper = styled.div`
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
  background: #ffffff;
  padding: 12px 10px;
  box-sizing: border-box;
  overflow: hidden;
`;

const TitleText = styled.div`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  margin-bottom: 10px;
`;

const SearchRow = styled.div`
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  box-sizing: border-box;

  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  outline: none;

  :focus {
    border-color: #ff8b48;
  }
`;

const TipBox = styled.div`
  margin-top: 10px;
  padding: 10px 10px;
  border: 1px solid #eee;
  border-radius: 6px;
`;

const TipTitle = styled.div`
  font-family: NotoSansKR-Regular;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const TipText = styled.div`
  font-family: NotoSansKR-Regular;
  font-size: 13px;
  line-height: 1.5;
  color: #333;
`;

const ResultWrapper = styled.div`
  margin-top: 10px;
  height: ${(p) => p.hasPredictions
    ? 'calc(100% - 44px - 10px - 20px)'
    : 'calc(100% - 44px - 10px - 110px - 10px)'};
  overflow: hidden;
`;

const StatusText = styled.div`
  font-family: NotoSansKR-Regular;
  font-size: 12px;
  color: #777;
  padding: 8px 2px;
`;

const ResultList = styled.div`
  height: 100%;
  overflow-y: auto;
  border-top: 1px solid #f2f2f2;
`;

const ResultItem = styled.div`
  padding: 10px 6px;
  border-bottom: 1px solid #f2f2f2;

  &:hover {
    cursor: pointer;
    background: #fafafa;
  }
`;

const MainText = styled.div`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  color: #000;
`;

const SubText = styled.div`
  font-family: NotoSansKR-Regular;
  font-size: 12px;
  color: #777;
  margin-top: 2px;
`;

const StatusBox = styled.div`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  font-family: NotoSansKR-Regular;
  font-size: 12px;
  color: #777;

  border: 1px dashed #ccc;
  border-radius: 6px;
`;
